import { useTranslation } from "react-i18next";
import DistrictTabs, {
  DistrictTabItem,
} from "src/grading/components/DistrictTabs/DistrictTabs";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import {
  rosterSourceKind,
  rosterSourceName,
} from "src/grading/utils/rosterSource";
import { OrganizationView } from "src/models/hierarchy";

export interface DistrictHeaderProps {
  districtId: string;
  district?: OrganizationView;
  /** Number of schools, when the page has loaded them (overview only). */
  schoolCount?: number;
}

/**
 * Header shared by every district tab (design `s.district`): "District admin" eyebrow, the
 * district name, a meta line (schools · sign-in · rostering) and the section tabs as a pill
 * tray at the right.
 */
export const DistrictHeader = ({
  districtId,
  district,
  schoolCount,
}: DistrictHeaderProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const isPlatformAdmin = hasPermissions(
    permissions.READ_DISTRICTS.action,
    permissions.READ_DISTRICTS.subject,
  );
  // People: a platform admin lands on this district's district admins, a district admin on Staff.
  const peopleTab: DistrictTabItem = {
    label: t("district.peopleTab"),
    to: isPlatformAdmin
      ? `/grading/people/district-admins?${new URLSearchParams({ district: districtId })}`
      : "/grading/people/staff",
    permission: permissions.READ_STAFF,
  };
  const ssoLabel = district?.ssoProvider ?? null;
  // FR-035: the vendor, or the last upload's kind ("CSV upload", "Roster sheet").
  const rosterKind = rosterSourceKind(district?.rosterSource);
  const rosterLabel =
    rosterKind === "none" ? null : rosterSourceName(t, rosterKind);
  const description = [
    schoolCount !== undefined
      ? t("district.schoolCount", {
          count: schoolCount,
          defaultValue: "{{count}} schools",
        })
      : null,
    ssoLabel
      ? t("district.ssoSummary", {
          provider: ssoLabel,
          defaultValue: "{{provider}} sign-in",
        })
      : null,
    rosterLabel
      ? t("district.rosterSummary", {
          provider: rosterLabel,
          defaultValue: "{{provider}} rostering",
        })
      : null,
    // FR-043: every district tab says when the license is suspended.
    (district?.license?.status ?? district?.licenseStatus) === "suspended"
      ? t("district.licenseSuspendedMeta")
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PageHeader
      eyebrow={t("district.eyebrow", { defaultValue: "District admin" })}
      title={
        district?.name ?? t("district.title", { defaultValue: "District" })
      }
      description={description || undefined}
      actions={<DistrictTabs districtId={districtId} extraTabs={[peopleTab]} />}
    />
  );
};

export default DistrictHeader;
